import React, { useEffect, useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  type AutocompleteInputChangeReason,
} from '@mui/material';

import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';

import {
  fetchAssignableUsers,
  type AssignableUser,
} from '@services/runStageApi.service';

import { searchEntityUsers } from '@services/editorApi.service';

export interface LaunchRunFormValue {
  startDate: string;
  endDate: string;
  tatValue: number | null;
  tatUnit:
    | 'seconds'
    | 'minutes'
    | 'hours'
    | 'days'
    | 'weeks'
    | null;
  assigneeIds: string[];
}

interface LaunchRunDialogProps {
  open: boolean;
  busy?: boolean;
  entityId: string;
  entityType: string;
  defaultAssigneeIds?: string[];
  onCancel: () => void;
  onSubmit: (value: LaunchRunFormValue) => void;
  onSuccess?: () => void;
}

const DEFAULT_UNIT: LaunchRunFormValue['tatUnit'] = 'days';

/**
 * Default datetime for datetime-local
 * YYYY-MM-DDTHH:mm
 */
const getDefaultStartDate = (): string => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const toInputDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const addTatToStart = (
  startDateValue: string,
  tatNumericValue: number,
  unit: NonNullable<LaunchRunFormValue['tatUnit']>,
): string => {
  const start = new Date(startDateValue);

  if (Number.isNaN(start.getTime())) {
    return '';
  }

  const end = new Date(start);

  switch (unit) {
    case 'seconds':
      end.setSeconds(
        end.getSeconds() + tatNumericValue,
      );
      break;

    case 'minutes':
      end.setMinutes(
        end.getMinutes() + tatNumericValue,
      );
      break;

    case 'hours':
      end.setHours(
        end.getHours() + tatNumericValue,
      );
      break;

    case 'days':
      end.setDate(
        end.getDate() + tatNumericValue,
      );
      break;

    case 'weeks':
      end.setDate(
        end.getDate() + tatNumericValue * 7,
      );
      break;
  }

  return toInputDateTime(end);
};

const msToTatUnit = (
  ms: number,
  unit: NonNullable<LaunchRunFormValue['tatUnit']>,
): number => {
  switch (unit) {
    case 'seconds':
      return Math.round(ms / 1000);

    case 'minutes':
      return Math.round(
        ms / (1000 * 60),
      );

    case 'hours':
      return Math.round(
        ms / (1000 * 60 * 60),
      );

    case 'weeks':
      return Math.round(
        ms / (1000 * 60 * 60 * 24 * 7),
      );

    case 'days':
    default:
      return Math.round(
        ms / (1000 * 60 * 60 * 24),
      );
  }
};

export const LaunchRunDialog: React.FC<
  LaunchRunDialogProps
> = ({
  open,
  busy = false,
  entityId,
  entityType,
  defaultAssigneeIds = [],
  onCancel,
  onSubmit,
  onSuccess,
}) => {
  const avatarColors = [
    '#3b82f6',
    '#6366f1',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
  ];

  const getAvatarColor = (
    name: string,
  ): string => {
    const index = name?.length % avatarColors.length;
    return avatarColors[index];
  };
  const [startDate, setStartDate] =
    useState(getDefaultStartDate);

  const [endDate, setEndDate] =
    useState('');

  const [tatValue, setTatValue] =
    useState<string>('');

  const [tatUnit, setTatUnit] =
    useState<LaunchRunFormValue['tatUnit']>(
      DEFAULT_UNIT,
    );

  const [assigneeSearchTerm, setAssigneeSearchTerm] =
    useState('');

  const [baseUserOptions, setBaseUserOptions] =
    useState<AssignableUser[]>([]);

  const [userOptions, setUserOptions] =
    useState<AssignableUser[]>([]);

  const [selectedUsers, setSelectedUsers] =
    useState<AssignableUser[]>([]);

  const [loadingUsers, setLoadingUsers] =
    useState(false);

  const recalculateEndDate = (
    nextStartDate: string,
    nextTatValue: string,
    nextTatUnit: LaunchRunFormValue['tatUnit'],
  ) => {
    const parsedTat = nextTatValue.trim()
      ? Number(nextTatValue)
      : null;

    if (
      parsedTat === null ||
      !Number.isFinite(parsedTat) ||
      parsedTat <= 0 ||
      !nextTatUnit ||
      !nextStartDate
    ) {
      setEndDate('');
      return;
    }

    setEndDate(
      addTatToStart(
        nextStartDate,
        parsedTat,
        nextTatUnit,
      ),
    );
  };

  const handleStartDateChange = (
    value: string,
  ) => {
    setStartDate(value);

    recalculateEndDate(
      value,
      tatValue,
      tatUnit,
    );
  };

  const handleTatValueChange = (
    value: string,
  ) => {
    if (
      value !== '' &&
      Number(value) < 0
    ) {
      value = '0';
    }

    setTatValue(value);

    recalculateEndDate(
      startDate,
      value,
      tatUnit,
    );
  };

  const handleTatUnitChange = (
    value: LaunchRunFormValue['tatUnit'],
  ) => {
    setTatUnit(value);

    recalculateEndDate(
      startDate,
      tatValue,
      value,
    );
  };

  const handleEndDateChange = (
    value: string,
  ) => {
    setEndDate(value);

    if (!value || !startDate) {
      return;
    }

    const start = new Date(startDate);
    const end = new Date(value);

    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      !tatUnit
    ) {
      return;
    }

    const diffMs =
      end.getTime() - start.getTime();

    if (diffMs > 0) {
      setTatValue(
        String(
          msToTatUnit(
            diffMs,
            tatUnit,
          ),
        ),
      );
    }
  };

  const mergeWithSelectedUsers = (
    options: AssignableUser[],
    selected: AssignableUser[],
  ) => {
    const byId = new Map<
      string,
      AssignableUser
    >();

    for (const option of options) {
      byId.set(option._id, option);
    }

    for (const selectedUser of selected) {
      if (
        !byId.has(selectedUser._id)
      ) {
        byId.set(
          selectedUser._id,
          selectedUser,
        );
      }
    }

    return Array.from(
      byId.values(),
    );
  };

  useEffect(() => {
    if (
      !open ||
      !entityId ||
      !entityType
    ) {
      return;
    }

    let cancelled = false;

    setLoadingUsers(true);

    fetchAssignableUsers(
      entityId,
      entityType,
    )
      .then((users) => {
        if (cancelled) {
          return;
        }

        const activeUsers =
          users.filter(
            (user) =>
              !user.removedAt,
          );

        setBaseUserOptions(
          activeUsers,
        );

        setUserOptions(
          mergeWithSelectedUsers(
            activeUsers,
            selectedUsers,
          ),
        );

        if (
          defaultAssigneeIds.length >
          0
        ) {
          const defaultUsers =
            activeUsers.filter(
              (user) =>
                defaultAssigneeIds.includes(
                  user._id,
                ),
            );

          setSelectedUsers(
            defaultUsers,
          );

          setUserOptions(
            mergeWithSelectedUsers(
              activeUsers,
              defaultUsers,
            ),
          );
        }
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setUserOptions([]);
      })
      .finally(() => {
        if (cancelled) {
          return;
        }

        setLoadingUsers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    entityId,
    entityType,
    defaultAssigneeIds,
  ]);

  useEffect(() => {
    if (
      !open ||
      !entityId
    ) {
      return;
    }

    const searchTerm =
      assigneeSearchTerm.trim();

    if (searchTerm.length === 0) {
      setUserOptions(
        mergeWithSelectedUsers(
          baseUserOptions,
          selectedUsers,
        ),
      );

      return;
    }

    let cancelled = false;

    setLoadingUsers(true);

    const timer =
      window.setTimeout(() => {
        const searchEntityType =
          'EXPERIMENT';

        searchEntityUsers(
          searchTerm,
          searchEntityType,
          entityId,
        )
          .then((users) => {
            if (cancelled) {
              return;
            }

            const normalized =
              users.map(
                (user) => ({
                  _id: user._id,
                  name: user.name,
                  email: user.email,
                }),
              );

            setUserOptions(
              mergeWithSelectedUsers(
                normalized,
                selectedUsers,
              ),
            );
          })
          .catch(() => {
            if (cancelled) {
              return;
            }

            setUserOptions(
              mergeWithSelectedUsers(
                baseUserOptions,
                selectedUsers,
              ),
            );
          })
          .finally(() => {
            if (cancelled) {
              return;
            }

            setLoadingUsers(false);
          });
      }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    open,
    entityId,
    assigneeSearchTerm,
    baseUserOptions,
    selectedUsers,
  ]);

  const handleAssigneeInputChange = (
    _: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (
      reason === 'input' ||
      reason === 'clear'
    ) {
      setAssigneeSearchTerm(value);
    }
  };

  const handleRemoveUser = (
    userId: string,
  ) => {
    const updatedUsers =
      selectedUsers.filter(
        (user) =>
          user._id !== userId,
      );

    setSelectedUsers(
      updatedUsers,
    );

    setUserOptions(
      mergeWithSelectedUsers(
        baseUserOptions,
        updatedUsers,
      ),
    );
  };

  const handleSubmit = () => {
    const parsedTat =
      tatValue.trim()
        ? Number(tatValue)
        : null;

    onSubmit({
      startDate,
      endDate,
      tatValue:
        parsedTat !== null &&
        Number.isFinite(parsedTat)
          ? parsedTat
          : null,
      tatUnit:
        parsedTat !== null &&
        parsedTat > 0
          ? tatUnit
          : null,
      assigneeIds:
        selectedUsers.map(
          (user) => user._id,
        ),
    });

    onSuccess?.();
  };

  return (
    <Dialog
      open={open}
      onClose={
        busy
          ? undefined
          : onCancel
      }
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width:
              '900px',
            maxWidth:
              'calc(100% - 32px)',
            height:
              'calc(100vh - 40px)',
            maxHeight: 'none',
            m: '20px auto',
            borderRadius: '4px',
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* ================= HEADER ================= */}

      <DialogTitle
        sx={{
          height: 60,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            'space-between',
          borderBottom:
            '1px solid #e5e7eb',
          fontSize: '18px',
          fontWeight: 600,
          backgroundColor:
            '#fafbfc',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <PersonAddAltOutlinedIcon
            sx={{
              fontSize: 20,
              color: 'primary.main',
            }}
          />

          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#263238',
            }}
          >
            Assign Users
          </Typography>
        </Box>

        <IconButton
          onClick={onCancel}
          disabled={busy}
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor:
                '#f5f5f5',
            },
          }}
        >
          <CloseIcon
            sx={{
              fontSize: 22,
            }}
          />
        </IconButton>
      </DialogTitle>

      {/* ================= CONTENT ================= */}

      <DialogContent
        sx={{
          p: 0,
          overflowY: 'auto',
          backgroundColor: '#fff',

          '&::-webkit-scrollbar': {
            width: '8px',
          },

          '&::-webkit-scrollbar-thumb': {
            backgroundColor:
              '#c8c8c8',
            borderRadius: '4px',
          },

          '&::-webkit-scrollbar-track': {
            backgroundColor:
              '#f5f5f5',
          },
        }}
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          {/* ================= ADD USER ================= */}

          <Paper
            elevation={0}
            sx={{
              border:
                '1px solid #e2e5e9',
              borderRadius: '10px',
              backgroundColor:
                '#fafbfc',
              p: 1.5,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                mb: 1,
              }}
            >
              <PersonAddAltOutlinedIcon
                sx={{
                  fontSize: 16,
                  color:
                    'primary.main',
                }}
              />

              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#424b57',
                }}
              >
                Add User
              </Typography>
            </Box>

            <Autocomplete
              multiple
              options={userOptions}
              value={selectedUsers}
              inputValue={
                assigneeSearchTerm
              }
              loading={loadingUsers}
              filterOptions={(
                options,
              ) => options}
              onInputChange={
                handleAssigneeInputChange
              }
              onChange={(
                _,
                value,
              ) => {
                setSelectedUsers(
                  value,
                );

                setAssigneeSearchTerm(
                  '',
                );

                setUserOptions(
                  mergeWithSelectedUsers(
                    baseUserOptions,
                    value,
                  ),
                );
              }}
              getOptionLabel={(
                option,
              ) => option.name
              }
              isOptionEqualToValue={(
                option,
                value,
              ) =>
                option._id ===
                value._id
              }
              /*
               * IMPORTANT:
               * renderTags is not supported
               * in your MUI version.
               *
               * renderValue is the new API.
               *
               * Returning null hides the selected
               * chips from the input. The selected
               * users are displayed in the table below.
               */
              renderValue={() => null}
              slotProps={{
                listbox: {
                  sx: {
                    fontSize:
                      '12px',
                    '& .MuiAutocomplete-option':
                      {
                        fontSize:
                          '12px !important',
                        padding:
                          '6px 8px !important',
                      },
                  },
                },
              }}
              renderInput={(
                params,
              ) => (
                <TextField
                  {...params}
                  placeholder="select user to add"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root':
                      {
                        minHeight: 36,
                        backgroundColor:
                          '#fff',
                        borderRadius:
                          '6px',
                      },

                    '& input': {
                      fontSize:
                        '12px',
                      padding:
                        '6px 8px !important',
                    },
                  }}
                />
              )}
            />
          </Paper>

          {/* ================= ASSIGNED USERS ================= */}

          <Box
            sx={{
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                mb: 1,
              }}
            >
              <FormatListBulletedOutlinedIcon
                sx={{
                  fontSize: 16,
                  color:
                    'primary.main',
                }}
              />

              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                Assigned Users
              </Typography>
            </Box>

            <Box
              sx={{
                border:
                  '1px solid #e1e4e8',
                overflow: 'hidden',
              }}
            >
              <Table
                size="small"
                sx={{
                  '& .MuiTableCell-root':
                    {
                      borderBottom:
                        '1px solid #e1e4e8',
                      height: 48,
                      padding: '6px 8px',
                    },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        '#fafafa',
                    }}
                  >
                    <TableCell
                      sx={{
                        width: '46%',
                        fontWeight: 600,
                        fontSize:
                          '13px',
                        color:
                          '#374151',
                      }}
                    >
                      <Box
                        sx={{
                          display:
                            'flex',
                          alignItems:
                            'center',
                          gap: 0.5,
                        }}
                      >
                        <PersonOutlineOutlinedIcon
                          sx={{
                            fontSize:
                              14,
                            color:
                              '#8a939e',
                          }}
                        />

                        Name
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{
                        width: '46%',
                        fontWeight: 600,
                        fontSize:
                          '13px',
                        color:
                          '#374151',
                      }}
                    >
                      <Box
                        sx={{
                          display:
                            'flex',
                          alignItems:
                            'center',
                          gap: 0.5,
                        }}
                      >
                        <MailOutlineOutlinedIcon
                          sx={{
                            fontSize:
                              14,
                            color:
                              '#8a939e',
                          }}
                        />

                        Email
                      </Box>
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        width: '8%',
                        fontWeight: 600,
                        fontSize:
                          '13px',
                        color:
                          '#374151',
                      }}
                    >
                      <Box
                        sx={{
                          display:
                            'flex',
                          alignItems:
                            'center',
                          justifyContent:
                            'center',
                          gap: 0.3,
                        }}
                      >
                        <SettingsOutlinedIcon
                          sx={{
                            fontSize:
                              14,
                            color:
                              '#8a939e',
                          }}
                        />

                        Action
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {selectedUsers.map(
                    (user, index) => (
                      <TableRow
                        key={
                          user._id
                        }
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display:
                                'flex',
                              alignItems:
                                'center',
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius:
                                  '50%',
                                backgroundColor:
                                  getAvatarColor(
                                    user.name,
                                  ),
                                color:
                                  '#fff',
                                display:
                                  'flex',
                                alignItems:
                                  'center',
                                justifyContent:
                                  'center',
                                fontWeight:
                                  600,
                                fontSize:
                                  '12px',
                                flexShrink:
                                  0,
                              }}
                            >
                              {user.name
                                .split(
                                  ' ',
                                )
                                .map(
                                  (
                                    part,
                                  ) =>
                                    part.charAt(
                                      0,
                                    ),
                                )
                                .join(
                                  '',
                                )
                                .slice(
                                  0,
                                  2,
                                )
                                .toUpperCase()}
                            </Box>

                            <Typography
                              sx={{
                                fontSize:
                                  '13px',
                                fontWeight:
                                  500,
                                color:
                                  '#374151',
                              }}
                            >
                              {
                                user.name
                              }
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography
                            sx={{
                              fontSize:
                                '12px',
                              color:
                                '#777',
                            }}
                          >
                            {
                              user.email
                            }
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <IconButton
                            size="small"
                            disabled={
                              busy
                            }
                            onClick={() =>
                              handleRemoveUser(
                                user._id,
                              )
                            }
                            sx={{
                              border:
                                '1px solid #f1caca',
                              borderRadius:
                                '4px',
                              color:
                                '#ef4444',
                              width: 36,
                              height: 32,

                              '&:hover':
                                {
                                  backgroundColor:
                                    '#fff5f5',
                                },
                            }}
                          >
                            <DeleteOutlineOutlinedIcon
                              sx={{
                                fontSize:
                                  16,
                              }}
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ),
                  )}

                  {selectedUsers.length ===
                    0 && (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        align="center"
                        sx={{
                          py: 2,
                          color:
                            '#888',
                          fontSize:
                            '12px',
                        }}
                      >
                        No users assigned
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>

          {/* ================= TIMELINE ================= */}

          <Paper
            elevation={0}
            sx={{
              border:
                '1px solid #e2e5e9',
              borderRadius: '10px',
              backgroundColor:
                '#fafbfc',
              p: 1.5,
              mt: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                mb: 1.5,
              }}
            >
              <AccessTimeOutlinedIcon
                sx={{
                  fontSize: 16,
                  color:
                    'primary.main',
                }}
              />

              <Typography
                sx={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#374151',
                }}
              >
                Timeline
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  '1.2fr 1.2fr 1.2fr',
                gap: 1.2,
                alignItems:
                  'start',

                '@media (max-width: 900px)':
                  {
                    gridTemplateColumns:
                      '1fr',
                  },
              }}
            >
              {/* ================= START DATE ================= */}

              <Box>
                <Typography
                  sx={{
                    fontSize:
                      '13px',
                    fontWeight:
                      600,
                    color:
                      '#4b5563',
                    mb: 0.6,
                  }}
                >
                  Start Date
                </Typography>

                <TextField
                  fullWidth
                  type="datetime-local"
                  size="small"
                  value={
                    startDate
                  }
                  onChange={(e) =>
                    handleStartDateChange(
                      e.target
                        .value,
                    )
                  }
                  sx={{
                    '& .MuiOutlinedInput-root':
                      {
                        height: 36,
                        backgroundColor:
                          '#fff',
                        borderRadius:
                          '5px',
                      },

                    '& input': {
                      fontSize:
                        '12px',
                      padding:
                        '6px 8px',
                    },
                  }}
                />

                <Typography
                  sx={{
                    fontSize:
                      '11px',
                    color:
                      '#8a8f98',
                    mt: 0.4,
                  }}
                >
                  When the task starts
                </Typography>
              </Box>

              {/* ================= TAT ================= */}

              <Box>
                <Typography
                  sx={{
                    fontSize:
                      '13px',
                    fontWeight:
                      600,
                    color:
                      '#4b5563',
                    mb: 0.6,
                  }}
                >
                  TAT Duration
                </Typography>

                <Box
                  sx={{
                    display:
                      'grid',
                    gridTemplateColumns:
                      '1fr 100px',
                    gap: 0.8,
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    placeholder="Enter TAT"
                    value={
                      tatValue
                    }
                    onChange={(e) =>
                      handleTatValueChange(
                        e.target
                          .value,
                      )
                    }
                    slotProps={{
                      htmlInput:
                        {
                          min: 0,
                          step: 1,
                        },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root':
                        {
                          height: 36,
                          backgroundColor:
                            '#fff',
                          borderRadius:
                            '5px',
                        },

                      '& input': {
                        fontSize:
                          '12px',
                        padding:
                          '6px 8px',
                      },
                    }}
                  />

                  <TextField
                    select
                    size="small"
                    value={
                      tatUnit ??
                      DEFAULT_UNIT
                    }
                    onChange={(e) =>
                      handleTatUnitChange(
                        e.target
                          .value as LaunchRunFormValue['tatUnit'],
                      )
                    }
                    sx={{
                      '& .MuiOutlinedInput-root':
                        {
                          height: 36,
                          backgroundColor:
                            '#fff',
                          borderRadius:
                            '5px',
                        },

                      '& .MuiSelect-select':
                        {
                          fontSize:
                            '12px',
                          padding:
                            '6px 8px',
                        },
                    }}
                  >
                    <MenuItem value="seconds">
                      Seconds
                    </MenuItem>

                    <MenuItem value="minutes">
                      Minutes
                    </MenuItem>

                    <MenuItem value="hours">
                      Hours
                    </MenuItem>

                    <MenuItem value="days">
                      Days
                    </MenuItem>

                    <MenuItem value="weeks">
                      Weeks
                    </MenuItem>
                  </TextField>
                </Box>

                <Typography
                  sx={{
                    fontSize:
                      '11px',
                    color:
                      '#8a8f98',
                    mt: 0.4,
                  }}
                >
                  Duration unit
                </Typography>
              </Box>

              {/* ================= END DATE ================= */}

              <Box>
                <Typography
                  sx={{
                    fontSize:
                      '13px',
                    fontWeight:
                      600,
                    color:
                      '#4b5563',
                    mb: 0.6,
                  }}
                >
                  End Date
                </Typography>

                <TextField
                  fullWidth
                  type="datetime-local"
                  size="small"
                  value={
                    endDate
                  }
                  onChange={(e) =>
                    handleEndDateChange(
                      e.target
                        .value,
                    )
                  }
                  sx={{
                    '& .MuiOutlinedInput-root':
                      {
                        height: 36,
                        backgroundColor:
                          '#fff',
                        borderRadius:
                          '5px',
                      },

                    '& input': {
                      fontSize:
                        '12px',
                      padding:
                        '6px 8px',
                    },
                  }}
                />

                <Typography
                  sx={{
                    fontSize:
                      '11px',
                    color:
                      '#8a8f98',
                    mt: 0.4,
                  }}
                >
                  Calculated automatically
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      {/* ================= FOOTER ================= */}

      <DialogActions
        sx={{
          minHeight: 70,
          px: 3,
          py: 1.5,
          borderTop:
            '1px solid #e5e7eb',
          backgroundColor: '#fff',
          gap: 1,
        }}
      >
        <Button
          onClick={onCancel}
          disabled={busy}
          sx={{
            minWidth: 70,
            height: 36,
            fontSize: '13px',
            textTransform:
              'none',
            color: '#555',
            border:
              '1px solid #d5d8dc',
            borderRadius: '4px',
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={busy}
          sx={{
            minWidth: 110,
            height: 36,
            fontSize: '13px',
            fontWeight: 600,
            textTransform:
              'none',
            borderRadius: '4px',
            boxShadow: 'none',
          }}
        >
          Assign Users
        </Button>
      </DialogActions>
    </Dialog>
  );
};